import { ListItemButton, ListItemIcon, Popover } from '@mui/material';
// @mui
import { alpha, styled } from '@mui/material/styles';

//
import type { NavItemProps } from '../types';

// config
import { ICON } from '../../../config-global';
// utils
import { bgBlur } from '../../../utils/cssStyles';

// ----------------------------------------------------------------------

type StyledItemProps = Omit<NavItemProps, 'item'>;

export const StyledItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'open',
})<StyledItemProps>(({ active, depth, disabled, open, theme }) => {
  const isLight = theme.palette.mode === 'light';

  const subItem = depth !== 1;

  const activeStyle = {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    color: theme.palette.primary.main,
    ...(!isLight && {
      color: theme.palette.primary.light,
    }),
  };

  const activeSubStyle = {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  };

  const hoverStyle = {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  };

  return {
    '&:hover': hoverStyle,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    flexDirection: 'column',
    padding: theme.spacing(1, 0, 0.5, 0),
    textTransform: 'capitalize',
    // Sub item
    ...(subItem && {
      flexDirection: 'row',
      padding: theme.spacing(1),
    }),
    // Active item
    ...(active && {
      ...activeStyle,
      '&:hover': {
        ...activeStyle,
      },
    }),
    // Active sub item
    ...(subItem &&
      active && {
        ...activeSubStyle,
        '&:hover': {
          ...activeSubStyle,
        },
      }),
    // Open
    ...(open && !active && hoverStyle),
    // Disabled
    ...(disabled && {
      '&.Mui-disabled': {
        opacity: 0.64,
      },
    }),
  };
});

// ----------------------------------------------------------------------

export const StyledIcon = styled(ListItemIcon)({
  height: ICON.NAV_ITEM_MINI,
  marginBottom: 4,
  marginRight: 0,
  width: ICON.NAV_ITEM_MINI,
});

// ----------------------------------------------------------------------

export const StyledPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    borderRadius: Number(theme.shape.borderRadius) * 1.5,
    boxShadow: theme.customShadows.dropdown,
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1),
    pointerEvents: 'auto',
    width: 160,
    ...bgBlur({ color: theme.palette.background.default }),
  },
  pointerEvents: 'none',
}));
